import { useState, useEffect, useMemo } from 'react';
import { formatFranc, formatFrancAdaptive } from '../lib/utils';
import api from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import BackButton from '../components/BackButton';
import { Users } from 'lucide-react';
import { buildTeamRowAvatar } from '../lib/avatars';
import TeamWorthChart from '../components/TeamWorthChart';
import RivalInsights from '../components/RivalInsights';
import SeasonHistory from '../components/SeasonHistory';
import { StageArenaView } from './stage/StageArenaView';
import { STAGE_CONFIG, BOOSTER_ICONS, sortRosterSlots, buildArenaLayout, boostedCategories, boosterEndsAt, boosterTimeLeft } from './stage/stageConfig';

// 2026-07-21 (owner request — roster parity): rival rosters now render in
// the SAME 6×3 arena grid as the owner's Stage tab (captain crown, RIP
// pills, category headers) instead of a flat list — matching mobile's
// TeamProfileScreen grid. Read-only: no floaters, no drafting.
const NO_FLOATERS = {};
const noHugeFloater = () => false;
const dailyOf = (slot) => slot.entity?.stats?.daily_points || 0;

export default function TeamViewPage({ teamId, onBack, onEntityClick }) {
  const [team, setTeam] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [stageStyle] = useState(() => {
    if (typeof window === 'undefined') return 'theater';
    try { return localStorage.getItem('stage_style') || 'theater'; } catch { return 'theater'; }
  });

  useEffect(() => {
    if (!teamId) return undefined;
    // 2026-07-21 audit — cancellation guard: a stale response for a previous
    // teamId must never overwrite the current team's state.
    let on = true;
    setLoading(true);
    setLoadError('');
    api.get(`/teams/view/${teamId}`).then(res => {
      if (!on) return;
      setTeam(res.data.team);
      setLoading(false);
    }).catch((e) => {
      if (!on) return;
      // 403 = a league you're not in — say so instead of "Team not found".
      const d = e?.response?.data?.detail;
      setLoadError(e?.response?.status === 403 && d?.message ? d.message : '');
      setLoading(false);
    });
    // 2026-07-21 (owner request) — worth-over-time plot for rival teams.
    api.get(`/teams/${teamId}/value-history`)
      .then(res => { if (on) setHistory(res.data); })
      .catch(() => { if (on) setHistory(null); });
    return () => { on = false; };
  }, [teamId]);

  // 2026-07-26 (owner report) — manager's avatar (photo/DiceBear) now ships
  // on /teams/view/:id, so the drill-in header matches the rankings row.
  const avatarUrl = useMemo(() => (team ? buildTeamRowAvatar(team, null, 96) : null), [team]);

  const sortedSlots = useMemo(() => sortRosterSlots(team?.slots), [team?.slots]);
  // 2026-09-07 (owner report) — use the LEAGUE's roster shape (ships on
  // /teams/view) so a Rulers-only league renders 18 rulers, not a 6×3 grid.
  const slotConfig = team?.slot_config;
  const { slotsWithGrid, columnPlan } = useMemo(
    () => buildArenaLayout(sortedSlots, slotConfig || {}),
    [sortedSlots, slotConfig],
  );
  const activeBoosters = team?.active_boosters || [];
  const boostedCats = useMemo(() => boostedCategories(team?.active_boosters), [team?.active_boosters]);

  if (loading) {
    return <div className="p-4"><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  }

  if (!team) {
    return (
      <div className="p-4">
        {onBack && <BackButton onClick={onBack} />}
        <p className="text-center text-muted-foreground" data-testid="team-view-error">{loadError || 'Team not found'}</p>
      </div>
    );
  }

  const slots = team.slots || [];
  const stageConfig = STAGE_CONFIG[stageStyle] || STAGE_CONFIG.theater;

  return (
    <div className="p-4" data-testid="team-view-page">
      {onBack && <BackButton onClick={onBack} />}

      {/* Team Header */}
      <div className="bg-card border border-border/60 rounded-2xl p-5 mb-4 text-center">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={team.username || 'Manager'}
            className="h-14 w-14 rounded-full object-cover mx-auto mb-3 bg-primary/10"
            data-testid="team-view-avatar"
          />
        ) : (
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Users className="h-7 w-7 text-primary" />
          </div>
        )}
        <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{team.name}</h1>
        <p className="text-sm text-muted-foreground">by {team.username}</p>
        <div className="flex justify-center gap-4 mt-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Net Worth</p>
            <p className="text-lg font-bold tabular-nums" data-testid="team-view-net-worth">{formatFrancAdaptive(team.net_worth ?? team.total_points ?? 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-lg font-bold tabular-nums">{formatFranc(team.total_points)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">This Week</p>
            <p className="text-lg font-bold tabular-nums text-[hsl(var(--positive-text))]">{formatFranc(team.weekly_points, { sign: true })}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Roster</p>
            <p className="text-lg font-bold tabular-nums">{team.slot_count ?? slots.length}/{team.roster_size || 18}</p>
          </div>
        </div>
      </div>

      {/* Roster — read-only arena grid (Stage-tab parity). 2026-07-23 (owner
          report analysis) — a pre-finalize team's roster is deliberately
          hidden server-side (anti-scouting); render the reason instead of a
          confusing empty arena. */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Roster</h3>
        {/* 2026-09-07 (owner request) — rival's live booster chips (same as Stage). */}
        {activeBoosters.length > 0 && (
          <div className="flex items-center gap-1" data-testid="team-view-active-boosters">
            {activeBoosters.map((b) => {
              const info = BOOSTER_ICONS[b] || { emoji: '⚡', label: b };
              const left = boosterTimeLeft(boosterEndsAt(null, team?.booster_expiry?.[b]));
              return (
                <div key={b} className="flex items-center gap-0.5 bg-[hsl(var(--accent)/0.15)] rounded-full px-2 py-0.5" title={`${info.label} active`} data-testid={`team-view-booster-chip-${b}`}>
                  <span className="text-sm">{info.emoji}</span>
                  <span className="text-[8px] font-semibold text-[hsl(var(--accent-text))]">
                    {info.label}{left && left !== 'expired' ? ` · ${left}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {team.roster_hidden ? (
        <div
          className="bg-card border border-border/60 rounded-2xl p-6 text-center"
          data-testid="team-view-roster-hidden"
        >
          <p className="text-sm text-muted-foreground">
            {team.roster_hidden_reason || 'Draft is in progress. Rosters become visible after finalization.'}
          </p>
        </div>
      ) : (
      <div data-testid="team-view-arena">
        <StageArenaView
          stageConfig={stageConfig}
          columnPlan={columnPlan}
          slotsWithGrid={slotsWithGrid}
          floatersByEntity={NO_FLOATERS}
          liveDaily={dailyOf}
          hasHugeFloater={noHugeFloater}
          onEntityClick={(id) => onEntityClick(id, team)}
          boostedCategories={boostedCats}
        />
      </div>
      )}

      {/* Worth over time — net worth per gameweek (falls back to roster value) */}
      {history && (
        <div className="mt-4">
          <TeamWorthChart
            netWorthSeries={history.net_worth_series}
            rosterValueSeries={history.roster_value_series}
          />
        </div>
      )}

      {/* Rival Portfolio Insights — biggest movers + biggest events (2026-08-12).
          Hidden automatically for pre-finalize rosters. */}
      {!team.roster_hidden && (
        <RivalInsights teamId={teamId} onEntityClick={(id) => onEntityClick(id, team)} />
      )}

      <div className="mt-4"><SeasonHistory path={`/recaps/history/team/${teamId}`} /></div>
    </div>
  );
}
